import config from '../../config';
import { getAuthHeaders, handleResponse } from './common';

const releasePacketApi = {
  getFaxOptions: async (clientReferenceId) => {
    const res = await fetch(`${config.api.url}/clients/${clientReferenceId}/release-packets/fax-options`, { headers: getAuthHeaders() });
    return handleResponse(res);
  },

  previewFaxPacket: async (clientReferenceId, payload) => {
    const res = await fetch(`${config.api.url}/clients/${clientReferenceId}/release-packets/preview-fax`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(payload),
    });
    if (!res.ok) return handleResponse(res);
    const pageCount = Number(res.headers.get('X-Fax-Page-Count'));
    const faxNumber = res.headers.get('X-Fax-Recipient-Number');
    const recipientName = decodeURIComponent(res.headers.get('X-Fax-Recipient-Name') || '');
    if (!Number.isInteger(pageCount) || pageCount < 2 || !faxNumber || !recipientName) {
      throw new Error('The fax review is incomplete. Please reload and review again.');
    }
    return { blob: await res.blob(), pageCount, faxNumber, recipientName,
      isSandbox: res.headers.get('X-Fax-Sandbox')?.toLowerCase() === 'true' };
  },
  /**
   * Fetch available release packet profiles for a client.
   * GET /clients/{clientReferenceId}/release-packets/profiles
   *
   * Response: ReleaseProfileDto[]
   *   { slug: string, displayName: string, defaultReleaseReason: string }
   */
  getProfiles: async (clientReferenceId) => {
    const url = `${config.api.url}/clients/${clientReferenceId}/release-packets/profiles`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    return handleResponse(res);
  },

  /**
   * Preview a release packet — returns categorised attachments.
   * POST /clients/{clientReferenceId}/release-packets/preview
   *
   * Request body: ReleasePacketPreviewRequest
   *   {
   *     profileSlug: string (required),
   *     releaseReason?: string,
   *     structuredSelectionOverrides?: ChartSectionSelection, // supports laborRecordsObjectiveOnly
   *     structuredItemSelection?: {
   *       prenatalVisitReferenceIds?: string[] | null,
   *       labResultIds?: number[] | null,
   *     },
   *     includeRefIds?: string[],
   *     excludeRefIds?: string[],
   *     referralSubject?: string,
   *     referralRequest?: string,
   *   }
   *
   * Response: ReleasePacketPreviewResponse
   *   {
   *     auto:                PacketAttachmentDto[],
   *     optional:            PacketAttachmentDto[],
   *     excluded:            PacketAttachmentDto[],
   *     orderedPacket:       PacketOrderItem[],
   *     warnings:            string[],
   *   }
   *
   * PacketAttachmentDto:
   *   compositeReferenceId, sourceType, displayName, category, subtype?,
   *   isClinical, isSensitive, encounterDate?, uploadedAtUtc,
   *   estimatedPageCount, previewable, fileType, sourceLabel,
   *   requiresAcknowledgement
   *
   * PacketOrderItem (lightweight — only these fields are guaranteed):
   *   kind ("FaxCover" | "ReferralRequest" | "StructuredChart" | "Attachment"),
   *   compositeReferenceId? (null for StructuredChart items),
   *   displayName?,
   *   estimatedPages   ← note: "estimatedPages" not "estimatedPageCount"
   */
  previewPacket: async (clientReferenceId, payload) => {
    const url = `${config.api.url}/clients/${clientReferenceId}/release-packets/preview`;
    const res = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    return handleResponse(res);
  },

  previewPacketPdf: async (clientReferenceId, payload) => {
    const url = `${config.api.url}/clients/${clientReferenceId}/release-packets/preview-pdf`;
    const res = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      return handleResponse(res);
    }
    return res.blob();
  },

  /**
   * Generate and download a release packet PDF.
   * POST /clients/{clientReferenceId}/release-packets/generate
   *
   * Request body: ReleasePacketGenerateRequest (extends PreviewRequest)
   *   {
   *     profileSlug: string (required),
   *     releaseReason?: string,
   *     structuredSelectionOverrides?: ChartSectionSelection, // supports laborRecordsObjectiveOnly
   *     structuredItemSelection?: {
   *       prenatalVisitReferenceIds?: string[] | null,
   *       labResultIds?: number[] | null,
   *     },
   *     includeRefIds?: string[],
   *     excludeRefIds?: string[],
   *     referralSubject?: string,
   *     referralRequest?: string,
   *     sensitiveAcknowledgement?: {
   *       acknowledged: boolean,
   *       refIds: string[],   // compositeReferenceIds of each sensitive doc included
   *     },
   *   }
   *
   * If any sensitive documents are in the packet and sensitiveAcknowledgement is
   * missing or incomplete the backend returns 400 with code
   * "sensitive_acknowledgement_required" | "sensitive_acknowledgement_incomplete".
   *
   * Response: PDF blob (Content-Disposition: attachment; filename="...")
   * Returns: { blob: Blob, filename: string }
   */
  generatePacket: async (clientReferenceId, payload) => {
    const url = `${config.api.url}/clients/${clientReferenceId}/release-packets/generate`;
    const res = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      return handleResponse(res);
    }

    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') || '';
    const filenameMatch = disposition.match(/filename[^;=\n]*=["']?([^"';\n]+)/);
    const filename = filenameMatch
      ? filenameMatch[1]
      : `release-packet-${clientReferenceId}-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.pdf`;

    return { blob, filename };
  },

  /**
   * Send a release packet via fax to a saved facility.
   * POST /clients/{clientReferenceId}/release-packets/send-fax
   *
   * Request body: ReleasePacketSendFaxRequest (extends GenerateRequest)
   *   {
   *     profileSlug: string (required),
   *     releaseReason?: string,
   *     structuredSelectionOverrides?: ChartSectionSelection, // supports laborRecordsObjectiveOnly
   *     structuredItemSelection?: {
   *       prenatalVisitReferenceIds?: string[] | null,
   *       labResultIds?: number[] | null,
   *     },
   *     includeRefIds?: string[],
   *     excludeRefIds?: string[],
   *     sensitiveAcknowledgement?: { acknowledged: boolean, refIds: string[] },
   *     destinationFacilityId: number (required int — facility row ID, NOT a fax number),
   *     notes?: string,
   *   }
   */
  sendFaxPacket: async (clientReferenceId, payload) => {
    const url = `${config.api.url}/clients/${clientReferenceId}/release-packets/send-fax`;
    const res = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    return handleResponse(res);
  },
};

export default releasePacketApi;
